import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { openWhatsApp } from "@/siteConfig";
import { Chip, LineInput, DualCTA, Eyebrow, Reveal } from "@/components/bits";
import { useLang } from "@/i18n";

export const CONCIERGES = [
  { num: "01", key: "c1" },
  { num: "02", key: "c2" },
  { num: "03", key: "c3" },
  { num: "04", key: "c4" },
  { num: "05", key: "c5" },
  { num: "06", key: "c6" },
];

const CITIES = ["loc.bbsr", "loc.puri", "loc.ctc"];

const ConciergeCard = ({ agent, open, onToggle }) => {
  const { t } = useLang();
  return (
    <div
      data-testid={`concierge-card-${agent.num}`}
      className={`border transition-colors duration-300 ${
        open ? "border-[#B8894B]/70 bg-[#1A2A24]" : "border-[#1A2A24] bg-[#131F1A] hover:border-[#B8894B]/40"
      }`}
    >
      <button
        type="button"
        data-testid={`concierge-toggle-${agent.num}`}
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-6 p-6 md:p-8 text-left"
      >
        <div className="flex items-baseline gap-5 md:gap-8">
          <span className="font-mono-label text-xs text-[#B8894B]">{agent.num}</span>
          <div>
            <span className="font-display text-xl md:text-2xl text-[#EDE7DA] tracking-tight">
              {t(`${agent.key}.name`)}
            </span>
            <span className="block font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#EDE7DA]/45 mt-1.5">
              {t(`${agent.key}.aud`)}
            </span>
          </div>
        </div>
        {open ? (
          <Minus className="w-4 h-4 text-[#B8894B] shrink-0" />
        ) : (
          <Plus className="w-4 h-4 text-[#EDE7DA]/50 shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-6 md:px-8 pb-8 md:pb-10">
          <p className="font-display italic text-lg md:text-xl text-[#B8894B] tracking-tight md:pl-16">
            “{t(`${agent.key}.tag`)}”
          </p>
          <ul className="grid sm:grid-cols-2 gap-x-8 mt-6 md:pl-16">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <li
                key={n}
                className="font-light text-sm text-[#EDE7DA]/70 py-2.5 border-t border-[#0E1613]/60 flex items-center gap-3"
              >
                <span className="w-1 h-1 bg-[#5E8570] shrink-0" />
                {t(`${agent.key}.f${n}`)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export const DevelopersSection = ({ onBook }) => {
  const { t } = useLang();
  const [openIdx, setOpenIdx] = useState(0);
  const [form, setForm] = useState({
    builder_name: "",
    project_name: "",
    city: "",
    units: "",
    concierges: [],
    phone: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const toggleConcierge = (key) =>
    setForm((f) => ({
      ...f,
      concierges: f.concierges.includes(key)
        ? f.concierges.filter((c) => c !== key)
        : [...f.concierges, key],
    }));

  const canSubmit =
    form.builder_name.trim() && form.project_name.trim() && form.city && form.phone.trim();

  const summary = [
    t("wa.dev.title"),
    `${t("wa.builder")}: ${form.builder_name}`,
    `${t("wa.project")}: ${form.project_name}`,
    `${t("wa.city")}: ${t(form.city)}`,
    `${t("wa.units")}: ${form.units || "—"}`,
    `${t("wa.concierges")}: ${form.concierges.length ? form.concierges.map((k) => t(`${k}.name`)).join(", ") : t("wa.allSix")}`,
    `${t("wa.phone")}: ${form.phone}`,
  ].join("\n");

  const submit = () => {
    openWhatsApp(summary);
    setSubmitted(true);
  };

  return (
    <section
      id="developers"
      data-testid="developers-section"
      className="py-24 md:py-36 border-t border-[#1A2A24]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <Eyebrow>{t("dev.eyebrow")}</Eyebrow>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight text-[#EDE7DA] mt-5 leading-[1.1]">
            {t("dev.h1a")}{" "}
            <em className="italic text-[#B8894B]">{t("dev.h1em")}</em>
          </h2>
          <p className="font-light text-base md:text-lg text-[#EDE7DA]/65 leading-relaxed mt-6">
            {t("dev.body")}
          </p>
          <p className="font-mono-label text-[11px] tracking-[0.18em] uppercase text-[#5E8570] mt-6">
            {t("dev.position")}
          </p>
        </div>

        <div className="grid gap-3 mt-14">
          {CONCIERGES.map((agent, i) => (
            <Reveal key={agent.num} delay={i * 90}>
              <ConciergeCard
                agent={agent}
                open={openIdx === i}
                onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
              />
            </Reveal>
          ))}
        </div>

        <div
          data-testid="data-spine-callout"
          className="border border-[#1A2A24] border-l-2 border-l-[#B8894B] bg-[#131F1A] p-7 md:p-10 mt-10"
        >
          <Eyebrow>{t("spine.eyebrow")}</Eyebrow>
          <p className="font-light text-base md:text-lg text-[#EDE7DA]/70 leading-relaxed mt-4 max-w-3xl">
            {t("spine.body")}
          </p>
        </div>

        <div className="grid md:grid-cols-12 gap-12 md:gap-16 mt-20 md:mt-28">
          <div className="md:col-span-5">
            <Eyebrow>{t("dev.demo.eyebrow")}</Eyebrow>
            <h3 className="font-display text-2xl md:text-4xl tracking-tight text-[#EDE7DA] mt-5 leading-[1.15]">
              {t("dev.demo.h1a")}{" "}
              <em className="italic text-[#B8894B]">{t("dev.demo.h1em")}</em>
              {t("dev.demo.h1b")}
            </h3>
            <p className="font-light text-sm md:text-base text-[#EDE7DA]/60 leading-relaxed mt-5">
              {t("dev.demo.body")}
            </p>
          </div>
          <div className="md:col-span-7 border border-[#1A2A24] bg-[#131F1A] p-7 md:p-12">
            {submitted ? (
              <div data-testid="developer-inquiry-success">
                <Eyebrow>{t("succ.eyebrow")}</Eyebrow>
                <h4 className="font-display text-2xl text-[#EDE7DA] mt-4 tracking-tight">
                  {t("dev.success.h")}
                </h4>
                <p className="font-light text-sm text-[#EDE7DA]/60 leading-relaxed mt-4">
                  {t("dev.success.body")}
                </p>
                <div className="mt-8">
                  <DualCTA
                    prefix="developer-inquiry"
                    waMessage={summary}
                    onBook={onBook}
                    waLabel={t("cta.waAgain")}
                    bookLabel={t("cta.bookCall")}
                  />
                </div>
              </div>
            ) : (
              <div className="grid gap-8">
                <div className="grid sm:grid-cols-2 gap-8">
                  <LineInput
                    label={t("dev.builderLabel")}
                    testId="dev-builder-name-input"
                    value={form.builder_name}
                    onChange={(e) => setForm({ ...form, builder_name: e.target.value })}
                    placeholder={t("ph.builder")}
                  />
                  <LineInput
                    label={t("dev.projectLabel")}
                    testId="dev-project-name-input"
                    value={form.project_name}
                    onChange={(e) => setForm({ ...form, project_name: e.target.value })}
                    placeholder={t("ph.project")}
                  />
                </div>
                <div>
                  <span className="font-mono-label text-[10px] tracking-[0.25em] uppercase text-[#EDE7DA]/45">
                    {t("dev.cityLabel")}
                  </span>
                  <div className="flex flex-wrap gap-3 mt-3">
                    {CITIES.map((cKey) => (
                      <Chip
                        key={cKey}
                        testId={`dev-city-${cKey.split(".").pop()}`}
                        active={form.city === cKey}
                        onClick={() => setForm({ ...form, city: cKey })}
                      >
                        {t(cKey)}
                      </Chip>
                    ))}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-8">
                  <LineInput
                    label={t("dev.unitsLabel")}
                    testId="dev-units-input"
                    value={form.units}
                    onChange={(e) => setForm({ ...form, units: e.target.value })}
                    placeholder={t("ph.units")}
                  />
                  <LineInput
                    label={t("dev.phoneLabel")}
                    testId="dev-phone-input"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+91"
                  />
                </div>
                <div>
                  <span className="font-mono-label text-[10px] tracking-[0.25em] uppercase text-[#EDE7DA]/45">
                    {t("dev.pickLabel")}
                  </span>
                  <div className="flex flex-wrap gap-3 mt-3">
                    {CONCIERGES.map((c) => (
                      <Chip
                        key={c.num}
                        testId={`dev-concierge-${c.num}`}
                        active={form.concierges.includes(c.key)}
                        onClick={() => toggleConcierge(c.key)}
                      >
                        {c.num} {t(`${c.key}.name`)}
                      </Chip>
                    ))}
                  </div>
                </div>
                <div>
                  <button
                    type="button"
                    data-testid="developer-inquiry-submit"
                    disabled={!canSubmit}
                    onClick={submit}
                    className="font-mono-label text-[11px] tracking-[0.2em] uppercase bg-[#B8894B] text-[#0E1613] px-7 py-3.5 hover:bg-[#EDE7DA] transition-colors duration-300 disabled:opacity-30 disabled:hover:bg-[#B8894B]"
                  >
                    {t("dev.submit")}
                  </button>
                  <p className="font-mono-label text-[10px] tracking-[0.15em] uppercase text-[#EDE7DA]/40 mt-4">
                    {t("cta.waNote")}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
